/**
 * Microsoft Clarity SDK wrapper for Budgit web app.
 *
 * #1216 / F1 (RESEARCH/BUDGIT_WEBSITE_PRIVACY_BASELINE_2026_08_06.md):
 * - Authentication is NOT consent. Recording requires analytics consent
 *   (localStorage via analyticsConsent.js).
 * - Signed-in financial screens must not be recorded until masking is
 *   verified (Clarity dashboard). mayInitializeClarity({ isAuthenticated })
 *   blocks that path by default.
 * - No custom user id is sent. Clarity cannot delete one user's recordings
 *   (the only delete is the whole project), so erasure would have nothing
 *   to call. Identify is not used. #2592.
 * - Sensitive elements should use `data-clarity-mask="true"` once masking lands.
 *
 * @see Clarity setup and installation: https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-api
 * @see Clarity JavaScript API reference (web): https://learn.microsoft.com/en-us/clarity/javascript-api
 */

import { mayInitializeClarity } from './analyticsConsent';

let isInitialized = false;

/**
 * Initialize Clarity by dynamically injecting the tracking script.
 *
 * Subsequent calls are safely ignored — the script tag is only injected once
 * per page lifecycle even across logout/login cycles.
 *
 * @param {{ isAuthenticated?: boolean, allowAuthenticated?: boolean }} [opts]
 * @returns {boolean} Whether initialization succeeded
 */
export function initializeClarity(opts = {}) {
  if (isInitialized) return true;

  if (!mayInitializeClarity(opts)) {
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Clarity] Skipped — analytics consent denied/missing or authenticated recording blocked');
    }
    return false;
  }

  const projectId = process.env.REACT_APP_CLARITY_PROJECT_ID;
  if (!projectId) {
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Clarity] REACT_APP_CLARITY_PROJECT_ID not set — skipping initialization');
    }
    return false;
  }

  try {
    // Guard against duplicate script injection. The Clarity tag may already
    // be in the DOM if the user logged out and back in during the same page
    // session (logout does not remove the script element).
    const alreadyLoaded = document.querySelector('script[src*="clarity.ms/tag"]');
    if (alreadyLoaded) {
      isInitialized = true;
      if (process.env.NODE_ENV === 'development') {
        console.debug('[Clarity] Script already present in DOM — reusing');
      }
      return true;
    }

    // Clarity tracking snippet — creates the global `clarity()` command queue
    // and asynchronously loads the Clarity tag script. Fall back to
    // document.head when no existing <script> nodes (e.g. empty jsdom).
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i;
      y = l.getElementsByTagName(r)[0];
      if (y && y.parentNode) {
        y.parentNode.insertBefore(t, y);
      } else if (l.head) {
        l.head.appendChild(t);
      } else {
        l.documentElement.appendChild(t);
      }
    })(window, document, 'clarity', 'script', projectId);

    isInitialized = true;

    if (process.env.NODE_ENV === 'development') {
      console.debug('[Clarity] Initialized', { projectId });
    }
    return true;
  } catch (error) {
    console.error('[Clarity] Initialization failed', error);
    return false;
  }
}

/**
 * Do not identify the session.
 *
 * Microsoft Clarity stores a custom user id (hashed) and has no API to
 * delete that user's recordings. Deleting data means deleting the project.
 * This returns false and does not call clarity('identify'), whatever id
 * a caller passes. #2592.
 *
 * @param {string} [_userId] ignored
 * @returns {boolean} always false
 */
export function setClarityUserId(_userId) {
  return false;
}

/**
 * Clear Clarity user identity on logout.
 *
 * The web SDK does not expose a direct "startNewSession" / "clear user"
 * API like the mobile SDK. We use an `upgrade` call with a "logout"
 * marker so the current session is explicitly tagged as ended in Clarity.
 * Login does not identify the next user. #2592.
 *
 * Note: isInitialized is intentionally NOT reset here. The Clarity script
 * remains in the DOM across logout/login cycles within the same page
 * session, so initializeClarity() should detect it as already loaded.
 */
export function clearClarityUser() {
  if (!isInitialized || typeof window.clarity !== 'function') return;

  try {
    // Web Clarity does not expose a direct "startNewSession" / "clear user"
    // API like the mobile SDK. We use an `upgrade` call with a "logout"
    // marker so the current authenticated session is explicitly tagged as
    // ended in Clarity. On next login, initializeClarity() will skip
    // re-injection (script already in DOM) and will not set a custom user id.
    window.clarity('upgrade', 'logout');

    if (process.env.NODE_ENV === 'development') {
      console.debug('[Clarity] Session ended on logout');
    }
  } catch (error) {
    console.warn('[Clarity] Failed to clear user', error);
  }
}

/**
 * Set a custom tag on the Clarity session.
 * Useful for segmenting sessions by feature flags, subscription tier, etc.
 *
 * @param {string} key - Tag name
 * @param {string} value - Tag value
 */
export function setClarityTag(key, value) {
  if (!isInitialized || typeof window.clarity !== 'function') return;

  try {
    window.clarity('set', key, value);
  } catch (error) {
    console.warn('[Clarity] Failed to set tag', error);
  }
}

/**
 * Check if Clarity has been initialized in this session.
 */
export function isClarityInitialized() {
  return isInitialized;
}
